import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type NativeSelectItem,
    NativeSelect
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";

const NAME = "NativeSelect";

const USAGE_CODE = `const [language, setLanguage] = useState("");

<NativeSelect
    data={[
        {
            title: "Türkçe",
            value: "tr"
        },
        {
            title: "English",
            value: "en"
        }
    ]}
    spreadBehaviour="stretch"
    onChange={setLanguage}
    title="Language"
    value={language}
/>`;

const GROUPS_CODE = `<NativeSelect
    data={[
        {
            group: "Frequent",
            title: "İstanbul",
            value: "istanbul"
        },
        {
            group: "All cities",
            title: "Adana",
            value: "adana"
        }
    ]}
    autoComplete="address-level2"
    title="City"
/>`;

const LANGUAGES: Array<NativeSelectItem> = [
    {
        title: "Türkçe",
        value: "tr"
    },
    {
        title: "English",
        value: "en"
    },
    {
        title: "Deutsch",
        value: "de"
    },
    {
        title: "Español",
        value: "es"
    }
];

const CITIES = [
    {
        groupKey: "nativeSelectPage.groupFrequent",
        title: "İstanbul",
        value: "istanbul"
    },
    {
        groupKey: "nativeSelectPage.groupFrequent",
        title: "Ankara",
        value: "ankara"
    },
    {
        groupKey: "nativeSelectPage.groupFrequent",
        title: "İzmir",
        value: "izmir"
    },
    {
        groupKey: "nativeSelectPage.groupAll",
        title: "Adana",
        value: "adana"
    },
    {
        groupKey: "nativeSelectPage.groupAll",
        title: "Bursa",
        value: "bursa"
    },
    {
        groupKey: "nativeSelectPage.groupAll",
        title: "Konya",
        value: "konya"
    }
];

const DISTRICTS: Array<NativeSelectItem> = [
    {
        title: "Kadıköy",
        value: "kadikoy"
    },
    {
        title: "Beşiktaş",
        value: "besiktas"
    }
];

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("nativeSelectPage.groupsTitle"),
        id: "groups"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const NativeSelectPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        language,
        setLanguage
    ] = useState("");

    const cities: Array<NativeSelectItem> = CITIES.map((city) => ({
        group: t(city.groupKey),
        title: city.title,
        value: city.value
    }));

    const periods: Array<NativeSelectItem> = [
        {
            title: t("nativeSelectPage.periodMonthly"),
            value: "monthly"
        },
        {
            title: t("nativeSelectPage.periodYearly"),
            value: "yearly"
        }
    ];

    return <Article>
        <ComponentHeader
            lead={t("nativeSelectPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("nativeSelectPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("nativeSelectPage.overviewControl")}
            </Paragraph>
            <Preview
                caption={t("nativeSelectPage.overviewCaption")}
                isStacked={true}
            >
                <NativeSelect
                    hintText={language ? t("nativeSelectPage.languageChanged", [
                        language
                    ]) : t("nativeSelectPage.languageHint")}
                    title={t("nativeSelectPage.languageTitle")}
                    spreadBehaviour="stretch"
                    onChange={setLanguage}
                    data={LANGUAGES}
                    value={language}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("nativeSelectPage.platformNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("nativeSelectPage.groupsTitle")}
            id="groups"
        >
            <Paragraph>
                {t("nativeSelectPage.groupsText")}
            </Paragraph>
            <Preview
                caption={t("nativeSelectPage.groupsCaption")}
                isStacked={true}
            >
                <NativeSelect
                    title={t("nativeSelectPage.cityTitle")}
                    autoComplete="address-level2"
                    spreadBehaviour="stretch"
                    data={cities}
                />
            </Preview>
            <CodeBlock
                code={GROUPS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("nativeSelectPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <NativeSelect
                        spreadBehaviour="stretch"
                        initialValue="en"
                        data={LANGUAGES}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("nativeSelectPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("nativeSelectPage.statesCaption")}
                isStacked={true}
            >
                <NativeSelect
                    title={t("nativeSelectPage.languageTitle")}
                    spreadBehaviour="stretch"
                    isRequired={true}
                    data={LANGUAGES}
                />
                <NativeSelect
                    hintText={t("nativeSelectPage.periodHint")}
                    title={t("nativeSelectPage.periodTitle")}
                    spreadBehaviour="stretch"
                    isRequired={true}
                    data={periods}
                    type="danger"
                />
                <NativeSelect
                    placeholder={t("nativeSelectPage.districtPlaceholder")}
                    title={t("nativeSelectPage.districtTitle")}
                    spreadBehaviour="stretch"
                    isDisabled={true}
                    data={DISTRICTS}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("nativeSelectPage.a11yWeb"),
                    t("nativeSelectPage.a11yLabel"),
                    t("nativeSelectPage.a11yStates"),
                    t("nativeSelectPage.a11yPlaceholder"),
                    t("nativeSelectPage.a11yFocus"),
                    t("nativeSelectPage.a11yNative")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("nativeSelectPage.propData"),
                        type: "Array<NativeSelectItem>",
                        name: "data"
                    },
                    {
                        description: t("nativeSelectPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("nativeSelectPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("nativeSelectPage.propOnChange"),
                        type: "(value: string) => void",
                        name: "onChange"
                    },
                    {
                        description: t("nativeSelectPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("nativeSelectPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("nativeSelectPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("nativeSelectPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("nativeSelectPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("nativeSelectPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("nativeSelectPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("nativeSelectPage.propIsMinWidthEnabled"),
                        name: "isMinWidthEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("nativeSelectPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("nativeSelectPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("nativeSelectPage.propOnFocus"),
                        type: "() => void",
                        name: "onFocus"
                    },
                    {
                        description: t("nativeSelectPage.propOnBlur"),
                        type: "() => void",
                        name: "onBlur"
                    },
                    {
                        description: t("nativeSelectPage.propAutoComplete"),
                        name: "autoComplete",
                        type: "string"
                    },
                    {
                        description: t("nativeSelectPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        description: t("nativeSelectPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("nativeSelectPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("nativeSelectPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    NativeSelectItem
                </InlineCode>
                {" "}
                {t("nativeSelectPage.itemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("nativeSelectPage.itemValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("nativeSelectPage.itemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("nativeSelectPage.itemGroup"),
                        type: "string",
                        name: "group"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default NativeSelectPage;
